// src/components/admin/OrderConsole.js - 注文管理のみ（支払い管理は設定ページへ移管）
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDatabase, ref, onValue, update } from 'firebase/database';
import { contentUsersDatabase } from '../../firebase';
import AdminOrderItem from './AdminOrderItem';
import shared from '../../styles/shared.module.css';
import styles from './Admin.module.css';

const TAB_UNPROCESSED = 'unprocessed';
const TAB_IN_PROGRESS = 'in_progress';
const TAB_COMPLETED = 'completed';

const OrderConsole = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(TAB_UNPROCESSED);

  // 注文データをリアルタイムで取得
  useEffect(() => {
    setLoading(true);
    const db = getDatabase();
    const ordersRef = ref(db, 'orders');

    const unsubscribe = onValue(ordersRef, (snapshot) => {
      try {
        const ordersData = snapshot.val();
        if (ordersData) {
          // オブジェクトを配列に変換し、IDを追加
          const ordersArray = Object.keys(ordersData).map(key => ({
            id: key,
            ...ordersData[key]
          }));

          // 未完了の注文を日時の新しい順にソート
          const sortedOrders = ordersArray.sort((a, b) =>
            new Date(b.orderedAt) - new Date(a.orderedAt)
          );

          setOrders(sortedOrders);
        } else {
          setOrders([]);
        }
      } catch (error) {
        console.error('注文データの取得エラー:', error);
        setError('注文データの取得中にエラーが発生しました。');
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // 注文のステータスを更新
  const handleUpdateOrderStatus = async (orderId, newStatus, isReady = false) => {
    try {
      const db = getDatabase();
      const orderRef = ref(db, `orders/${orderId}`);

      const updates = {
        status: newStatus
      };

      // ステータスに応じた追加フィールドの更新
      if (newStatus === 'preparation') {
        updates.isReady = false;
        updates.preparingStartedAt = new Date().toISOString();
      } else if (newStatus === 'Finish') {
        updates.isReady = true; // isReadyを常にtrueに設定
        updates.readyAt = new Date().toISOString();
      }

      await update(orderRef, updates);
      console.log(`注文 ${orderId} のステータスを ${newStatus} に更新しました`);

    } catch (error) {
      console.error('注文ステータス更新エラー:', error);
      setError('注文ステータスの更新中にエラーが発生しました。');
    }
  };

  // 処理中の注文を強制的に受け取り完了にする（ユーザーが受け取り操作を忘れた場合用）
  const handleMarkAsPickedUp = async (order) => {
    if (!order?.id || !order?.userId) return;
    try {
      const db = getDatabase();
      const timestamp = new Date().toISOString();
      await update(ref(db, `orders/${order.id}`), {
        isPickedUp: true,
        status: 'completed',
        pickedUpAt: timestamp
      });
      await update(ref(db, `orderHistory/${order.userId}/${order.id}`), {
        isPickedUp: true,
        status: 'completed',
        pickedUpAt: timestamp
      });
      await update(ref(contentUsersDatabase, `users/profiles/${order.userId}`), {
        lastActiveOrderId: null,
        lastPickupAt: timestamp,
        updatedAt: timestamp
      });
    } catch (err) {
      console.error('受け取り完了の更新エラー:', err);
      setError('受け取り完了の更新中にエラーが発生しました。');
    }
  };

  // 未処理: まだ準備が完了していない注文
  const unprocessedOrders = orders.filter(order => !order.isReady && !order.isPickedUp);
  // 処理中: 準備完了・商品引渡し前の注文
  const inProgressOrders = orders.filter(order => order.isReady && !order.isPickedUp);
  // 完了: ユーザーが受け取った注文
  const completedOrders = orders.filter(order => order.isPickedUp);

  // 完了注文を日付でグループ化（ユーザーが注文した日時＝orderedAt で統一）
  const getOrderDateKey = (order) => {
    const iso = order.orderedAt || '';
    return iso ? iso.split('T')[0] : '';
  };
  const formatDateLabel = (dateKey) => {
    if (!dateKey) return '';
    const [y, m, d] = dateKey.split('-').map(Number);
    return `${y}年${m}月${d}日`;
  };
  const formatTime = (isoString) => {
    if (!isoString) return '--:--';
    const d = new Date(isoString);
    return new Intl.DateTimeFormat('ja-JP', { hour: '2-digit', minute: '2-digit' }).format(d);
  };
  // 注文日時のタイムスタンプを取得（ISO文字列・数値・Firebase形式に対応）
  const getOrderedAtTime = (order) => {
    const v = order.orderedAt;
    if (v == null || v === '') return 0;
    if (typeof v === 'number') return v;
    if (typeof v === 'string') return new Date(v).getTime();
    if (typeof v === 'object' && v !== null && typeof v.seconds === 'number') return v.seconds * 1000;
    return 0;
  };

  const completedByDate = completedOrders.reduce((acc, order) => {
    const key = getOrderDateKey(order);
    if (!key) return acc;
    if (!acc[key]) acc[key] = [];
    acc[key].push(order);
    return acc;
  }, {});
  // 各日付グループ内を注文日時（orderedAt）の降順でソート（新しい順）。コピーをソートして代入し確実に反映
  Object.keys(completedByDate).forEach(dateKey => {
    completedByDate[dateKey] = [...completedByDate[dateKey]].sort(
      (a, b) => getOrderedAtTime(b) - getOrderedAtTime(a)
    );
  });
  const completedDateKeys = Object.keys(completedByDate).sort((a, b) => b.localeCompare(a));

  // ダッシュボードに戻る処理
  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.headerContainer}>
          <h1 className={styles.headerTitle}>BANNAI'S BAR - 注文管理</h1>
          <button
            onClick={handleBackToDashboard}
            className={styles.backButton}
          >
            戻る
          </button>
        </div>
      </header>

      <main className={styles.mainContent}>
        {/* タブ: 未処理 / 処理中 / 完了（固定） */}
        <div className={styles.tabMenu}>
          <button
            type="button"
            className={`${styles.tabButton} ${activeTab === TAB_UNPROCESSED ? styles.tabButtonActive : ''}`}
            onClick={() => setActiveTab(TAB_UNPROCESSED)}
          >
            未処理
            {unprocessedOrders.length > 0 && (
              <span className={styles.badge}>{unprocessedOrders.length}</span>
            )}
          </button>
          <button
            type="button"
            className={`${styles.tabButton} ${activeTab === TAB_IN_PROGRESS ? styles.tabButtonActive : ''}`}
            onClick={() => setActiveTab(TAB_IN_PROGRESS)}
          >
            処理中
            {inProgressOrders.length > 0 && (
              <span className={styles.badge}>{inProgressOrders.length}</span>
            )}
          </button>
          <button
            type="button"
            className={`${styles.tabButton} ${activeTab === TAB_COMPLETED ? styles.tabButtonActive : ''}`}
            onClick={() => setActiveTab(TAB_COMPLETED)}
          >
            完了
            {completedOrders.length > 0 && (
              <span className={styles.badge}>{completedOrders.length}</span>
            )}
          </button>
        </div>

        {/* タブの中身のみスクロール */}
        <div className={styles.tabContentScroll}>
          {error && (
            <div className={styles.errorMessage}>
              {error}
              <button
                onClick={() => setError('')}
                className={styles.closeButton}
              >
                ×
              </button>
            </div>
          )}

          {loading ? (
            <div className={shared.loadingSpinnerInline}>
              <div className={shared.spinner}></div>
            </div>
          ) : (
            <>
              {activeTab === TAB_UNPROCESSED && (
              unprocessedOrders.length === 0 ? (
                <div className={styles.emptyState}>
                  <p className={styles.emptyStateText}>未処理の注文はありません</p>
                </div>
              ) : (
                <div className={styles.sectionList}>
                  {unprocessedOrders.map(order => (
                    <AdminOrderItem
                      key={order.id}
                      order={order}
                      onUpdateStatus={handleUpdateOrderStatus}
                      onMarkAsPickedUp={handleMarkAsPickedUp}
                    />
                  ))}
                </div>
              )
            )}
            {activeTab === TAB_IN_PROGRESS && (
              inProgressOrders.length === 0 ? (
                <div className={styles.emptyState}>
                  <p className={styles.emptyStateText}>
                    準備完了・受け取り待ちの注文はありません
                  </p>
                </div>
              ) : (
                <div className={styles.sectionList}>
                  {inProgressOrders.map(order => (
                    <AdminOrderItem
                      key={order.id}
                      order={order}
                      onUpdateStatus={handleUpdateOrderStatus}
                      onMarkAsPickedUp={handleMarkAsPickedUp}
                    />
                  ))}
                </div>
              )
            )}
            {activeTab === TAB_COMPLETED && (
              completedOrders.length === 0 ? (
                <div className={styles.emptyState}>
                  <p className={styles.emptyStateText}>
                    受け取り完了した注文はありません
                  </p>
                </div>
              ) : (
                <div className={styles.completedSection}>
                  {completedDateKeys.map(dateKey => (
                    <div key={dateKey} className={styles.completedDateGroup}>
                      <h3 className={styles.completedDateHeading}>{formatDateLabel(dateKey)}</h3>
                      <div className={styles.completedOrderList}>
                        {(completedByDate[dateKey] || []).map(order => (
                          <div key={order.id} className={styles.completedOrderRow}>
                            <span className={styles.completedOrderTime}>
                              {formatTime(order.pickedUpAt || order.orderedAt)}
                            </span>
                            <span className={styles.completedOrderName}>{order.productName}</span>
                            <span className={styles.completedOrderCustomer}>
                              ({order.userName || order.userEmail || '—'})
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </>
          )}
        </div>
      </main>
    </div>
  );
};

export default OrderConsole;